// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerDataObjectDropdown.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Data object Dropdown component
 */
import { action } from 'mobx';
import React, { Component, PropTypes } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import DropDown from 'share/components/controls/Izenda.DropDown';
import Validator from 'share/components/hoc/Izenda.Validator';
import { isEmpty, filter, orderBy, find, flow, head, some, isEqual } from 'lodash/fp';

const DropDownValidator = Validator()(DropDown);

@connect()
export default class ReportDesignerDataObjectDropdown extends Component {
  static propTypes = {
    dataObjects: PropTypes.array,
    options: PropTypes.array.isRequired,
    dataTree: PropTypes.object.isRequired,
    dataSourceItem: PropTypes.object.isRequired,
    dataSourceDetail: PropTypes.object.isRequired,
    fieldName: PropTypes.string.isRequired
  };

  componentWillReceiveProps(nextProps: any) {
    const { options, fieldName: key, dataSourceItem, dataSourceItem: { isKeyJoin } } = this.props;

    if (!isEqual(nextProps.options, options) && isKeyJoin) {
      const querySourcePropertyName = this.getQuerySourcePropertyName();
      const currentValue = dataSourceItem[querySourcePropertyName];
      if (!some({ value: currentValue }, nextProps.options)) {
        const updateFn =
          key === constants.TABLE_DEF.ADVANCED_MODE.dataObject.key
            ? dataSourceItem.setJoinQuerySource
            : dataSourceItem.setForeignQuerySource;
        updateFn.call(dataSourceItem, null, null);
      }
    }
  }

  getQuerySourcePropertyName() {
    const { dataSourceItem: { isKeyJoin }, fieldName: key } = this.props;

    if (isKeyJoin) {
      return key === constants.TABLE_DEF.ADVANCED_MODE.dataObject.key
        ? constants.TABLE_DEF.ADVANCED_MODE.selectedJoinSourceAlias.key
        : constants.TABLE_DEF.ADVANCED_MODE.selectedForeignSourceAlias.key;
    } else {
      return key;
    }
  }

  /**
   * Get the first primary key base on dataObjects
   * @param  {object} currentDataObject
   * @return {string} id of primary field
   */
  getPrimaryKeyByDataObject(currentDataObject: any) {
    if (!isEmpty(currentDataObject)) {
      return flow(
        filter(field => {
          const ext = !isEmpty(field.extendedProperties) ? JSON.parse(field.extendedProperties) : {};
          return !isEmpty(ext) && ext.PrimaryKey;
        }),
        orderBy(['name'], ['asc']),
        head
      )(currentDataObject.fields);
    }
  }

  @action('setQuerySourceAndPrimaryKey')
  setQuerySourceAndPrimaryKey(primaryKey: any, fieldName: string, dataSourceItem: any, objectChanged: any = {}) {
    if (fieldName === constants.TABLE_DEF.ADVANCED_MODE.dataObject.key) {
      dataSourceItem.setJoinQuerySource(objectChanged.value, objectChanged.label);
      !isEmpty(primaryKey) && dataSourceItem.setJoinField(primaryKey.id, primaryKey.name);
    } else {
      dataSourceItem.setForeignQuerySource(objectChanged.value, objectChanged.label, objectChanged.isDataSourceAlias);
      !isEmpty(primaryKey) && dataSourceItem.setForeignField(primaryKey.id, primaryKey.name);
    }
  }

  @action('changeDataObject')
  changeDataObject = (object: any, selectedItem: any) => {
    const {
      store,
      dataSourceDetail,
      dataSourceItem,
      dataSourceItem: { isKeyJoin, joinType },
      fieldName: key,
      dataObjects,
      checkJoinTypeEntities
    } = this.props;

    let currentDataObject;
    const dataObjectKey = constants.TABLE_DEF.ADVANCED_MODE.dataObject.key;

    if (isKeyJoin) {
      currentDataObject = find({ aliasId: object.value }, dataObjects);
    } else {
      currentDataObject =
        key === dataObjectKey ? find({ id: object.value }, dataObjects) : find({ aliasId: object.value }, dataObjects);
    }

    const primaryKey = this.getPrimaryKeyByDataObject(currentDataObject);

    if (joinType !== constants.JOIN_TYPE_CROSS) {
      this.setQuerySourceAndPrimaryKey(primaryKey, key, dataSourceItem, selectedItem);
    } else {
      const updateDataObjectFn =
        key === dataObjectKey ? dataSourceItem.setJoinQuerySource : dataSourceItem.setForeignQuerySource;

      updateDataObjectFn.call(dataSourceItem, object.value, object.label);
    }

    // generate alias if DataObject is Relationship
    if (!isKeyJoin) {
      dataSourceDetail.onDataObjectChangeToGenerateAlias(dataSourceItem, key);
    }

    checkJoinTypeEntities && checkJoinTypeEntities(this.props, store, dataSourceItem);
    store.reportStore.dataSourceStore.dataSourceDetail.markDataSourceGridDirty();
  };

  focusForeignDataObject = action('focusForeignDataObject', (input: any) => {
    const { dataSourceItem, fieldName, validation } = this.props;

    // Clear the violated alias value
    if (dataSourceItem.invalidAlias) {
      dataSourceItem.setForeignQuerySource(null, null);
      dataSourceItem.invalidAlias = null;
      validation.reset(fieldName, 'Relationships' + dataSourceItem.tempId);
    }
  });

  render() {
    const {
      options,
      dataTree,
      fieldName: key,
      dataSourceItem,
      dataSourceItem: { isJoinQuerySourceReady, isforeignQuerySourceReady, tempId }
    } = this.props;

    const dataObjectKey = constants.TABLE_DEF.ADVANCED_MODE.dataObject.key;
    // Disable when user does not selected category
    let disabled = key === dataObjectKey ? !isJoinQuerySourceReady : !isforeignQuerySourceReady;
    const isAllDataOjectUncategory = dataTree.isAllDataOjectUncategory;
    // If all data object uncategory,
    disabled = disabled && !isAllDataOjectUncategory;
    const querySourcePropertyName = this.getQuerySourcePropertyName();

    return (
      <DropDownValidator
        options={options}
        onChange={this.changeDataObject}
        onFocus={this.focusForeignDataObject}
        value={dataSourceItem[querySourcePropertyName]}
        disabled={disabled}
        valueField="value"
        labelField="labelDisplay"
        blankOption
        hideIcon
        showTooltip
        validations={constants.REQUIRED_CONSTRAINT}
        field={key}
        group={'Relationships' + tempId}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/DataSource/Izenda.ReportDesignerDataObjectDropdown.js